import { useEffect, useState } from "react";
import { MessageCircleQuestion, Send, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Stars } from "@/components/Stars";
import { FadeUp } from "@/components/Reveal";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });

export default function ProductFeedback({ product }) {
  const [reviews, setReviews] = useState([]);
  const [questions, setQuestions] = useState([]);

  const [rvName, setRvName] = useState("");
  const [rvRating, setRvRating] = useState(0);
  const [rvHover, setRvHover] = useState(0);
  const [rvComment, setRvComment] = useState("");
  const [rvSending, setRvSending] = useState(false);

  const [qName, setQName] = useState("");
  const [qText, setQText] = useState("");
  const [qSending, setQSending] = useState(false);

  const loadReviews = () =>
    api
      .get(`/products/${product.id}/reviews`)
      .then((r) => setReviews(r.data.reviews || []))
      .catch(() => setReviews([]));

  const loadQuestions = () =>
    api
      .get(`/products/${product.id}/questions`)
      .then((r) => setQuestions(r.data.questions || []))
      .catch(() => setQuestions([]));

  useEffect(() => {
    if (!product?.id) return;
    loadReviews();
    loadQuestions();
  }, [product?.id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!rvRating) {
      toast.error("Escolha de 1 a 5 estrelas.");
      return;
    }
    setRvSending(true);
    try {
      const { data } = await api.post(`/products/${product.id}/reviews`, {
        name: rvName,
        rating: rvRating,
        comment: rvComment,
      });
      setReviews(data.reviews || []);
      toast.success("Avaliação publicada. Obrigado!");
      setRvName("");
      setRvRating(0);
      setRvHover(0);
      setRvComment("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível enviar sua avaliação.");
    } finally {
      setRvSending(false);
    }
  };

  const submitQuestion = async (e) => {
    e.preventDefault();
    setQSending(true);
    try {
      await api.post(`/products/${product.id}/questions`, {
        name: qName,
        question: qText,
      });
      toast.success("Pergunta enviada! A loja vai responder em breve.");
      setQName("");
      setQText("");
      loadQuestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível enviar sua pergunta.");
    } finally {
      setQSending(false);
    }
  };

  const inputCls =
    "w-full h-11 rounded-md bg-ink/60 border border-white/15 px-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-cyan/60 transition-colors";

  return (
    <div className="grid lg:grid-cols-2 gap-10 mt-20" data-testid="product-feedback">
      <FadeUp>
        <section data-testid="reviews-section">
          <h2 className="font-display font-semibold tracking-tight text-2xl md:text-3xl">
            Avaliações
            {reviews.length > 0 && (
              <span className="text-base text-mute font-normal"> ({reviews.length})</span>
            )}
          </h2>
          <form
            onSubmit={submitReview}
            data-testid="review-form"
            className="mt-6 bg-elevated/60 border border-white/10 rounded-lg p-5 space-y-3.5"
          >
            <p className="text-sm text-mute">Já comprou? Deixe sua avaliação</p>
            <input
              value={rvName}
              onChange={(e) => setRvName(e.target.value)}
              placeholder="Seu nome"
              required
              minLength={2}
              maxLength={60}
              className={inputCls}
              data-testid="review-form-input-name"
            />
            <div className="flex gap-1" data-testid="review-star-picker">
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setRvRating(i)}
                  onMouseEnter={() => setRvHover(i)}
                  onMouseLeave={() => setRvHover(0)}
                  aria-label={`${i} estrela${i > 1 ? "s" : ""}`}
                  className="p-0.5 transition-transform duration-200 hover:scale-125"
                  data-testid={`review-star-${i}`}
                >
                  <Star
                    size={24}
                    fill={i <= (rvHover || rvRating) ? "currentColor" : "none"}
                    className={i <= (rvHover || rvRating) ? "text-amber-400" : "text-white/25"}
                  />
                </button>
              ))}
            </div>
            <textarea
              value={rvComment}
              onChange={(e) => setRvComment(e.target.value)}
              placeholder="Comente sobre o produto (opcional)"
              maxLength={600}
              rows={3}
              className={`${inputCls} h-auto py-2.5 resize-none`}
              data-testid="review-form-input-comment"
            />
            <button
              type="submit"
              disabled={rvSending}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-cyan text-ink text-sm font-semibold transition-all duration-300 hover:bg-cyan-dark disabled:opacity-40"
              data-testid="review-form-submit"
            >
              <Send size={14} />
              {rvSending ? "Enviando..." : "Enviar avaliação"}
            </button>
          </form>
          <ul className="mt-6 space-y-3" data-testid="reviews-list">
            {reviews.map((r) => (
              <li
                key={r.id}
                className="bg-elevated/40 border border-white/10 rounded-lg p-4"
                data-testid={`review-item-${r.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-white">{r.name}</span>
                  <span className="text-[11px] text-mute whitespace-nowrap">{fmtDate(r.created_at)}</span>
                </div>
                <Stars value={r.rating} size={13} className="mt-1" />
                {r.comment && <p className="text-sm text-mute leading-relaxed mt-2">{r.comment}</p>}
              </li>
            ))}
            {reviews.length === 0 && (
              <li
                className="text-sm text-mute border border-dashed border-white/15 rounded-lg p-6 text-center"
                data-testid="reviews-empty"
              >
                Ainda não há avaliações deste produto. Seja o primeiro a avaliar!
              </li>
            )}
          </ul>
        </section>
      </FadeUp>

      <FadeUp delay={0.08}>
        <section data-testid="questions-section">
          <h2 className="font-display font-semibold tracking-tight text-2xl md:text-3xl flex items-center gap-2.5">
            <MessageCircleQuestion size={26} className="text-cyan" />
            Perguntas
            {questions.length > 0 && (
              <span className="text-base text-mute font-normal"> ({questions.length})</span>
            )}
          </h2>
          <form
            onSubmit={submitQuestion}
            data-testid="question-form"
            className="mt-6 bg-elevated/60 border border-white/10 rounded-lg p-5 space-y-3.5"
          >
            <p className="text-sm text-mute">Tem alguma dúvida? Pergunte à loja</p>
            <input
              value={qName}
              onChange={(e) => setQName(e.target.value)}
              placeholder="Seu nome"
              required
              minLength={2}
              maxLength={60}
              className={inputCls}
              data-testid="question-form-input-name"
            />
            <textarea
              value={qText}
              onChange={(e) => setQText(e.target.value)}
              placeholder="Escreva sua pergunta"
              required
              minLength={5}
              maxLength={500}
              rows={3}
              className={`${inputCls} h-auto py-2.5 resize-none`}
              data-testid="question-form-input-text"
            />
            <button
              type="submit"
              disabled={qSending}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-cyan text-ink text-sm font-semibold transition-all duration-300 hover:bg-cyan-dark disabled:opacity-40"
              data-testid="question-form-submit"
            >
              <Send size={14} />
              {qSending ? "Enviando..." : "Enviar pergunta"}
            </button>
          </form>
          <ul className="mt-6 space-y-3" data-testid="questions-list">
            {questions.map((q) => (
              <li
                key={q.id}
                className="bg-elevated/40 border border-white/10 rounded-lg p-4"
                data-testid={`question-item-${q.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-white">{q.name}</span>
                  <span className="text-[11px] text-mute whitespace-nowrap">{fmtDate(q.created_at)}</span>
                </div>
                <p className="text-sm text-white/85 leading-relaxed mt-2">{q.question}</p>
                {q.answer ? (
                  <div className="mt-3 rounded-md bg-cyan/5 border border-cyan/25 p-3">
                    <p className="text-[11px] uppercase tracking-[0.16em] text-cyan mb-1">Resposta da loja</p>
                    <p className="text-sm text-white/85 leading-relaxed">{q.answer}</p>
                  </div>
                ) : (
                  <p className="text-xs text-mute mt-3 italic" data-testid={`question-pending-${q.id}`}>
                    Aguardando resposta da loja...
                  </p>
                )}
              </li>
            ))}
            {questions.length === 0 && (
              <li
                className="text-sm text-mute border border-dashed border-white/15 rounded-lg p-6 text-center"
                data-testid="questions-empty"
              >
                Nenhuma pergunta ainda. Ficou com dúvida? Pergunte aqui!
              </li>
            )}
          </ul>
        </section>
      </FadeUp>
    </div>
  );
}
